import * as React from "react"
import { useCallback, useMemo, useEffect, useRef } from "react"
import styled from "styled-components/macro"
import {
  MapState,
  Place,
  useMapStore,
} from "@common/features/GoogleMaps/mapStore"
import DirectionsPanel from "./DirectionsPanel"
import IconLabel from "@common/ui/IconLabel"
import { averageDistance } from "@lib/utils/google.maps"
import TextButton from "@common/ui/TextButton"
import { useStore } from "@state/store"

const categoriesZoomConfig = {
  connections: {
    coords: {
      lat: 51.49419958241411,
      lng: 0.05474787151563021,
    },
    zoom: 15,
  },
  "Food and Beverage": {
    coords: {
      lat: 51.49419958241411,
      lng: 0.05474787151563021,
    },
    zoom: 15,
  },

  "Wellness and Leisure": {
    coords: {
      lat: 51.48760620725085,
      lng: 0.04293921118797783,
    },
    zoom: 14,
  },
  "Arts and Culture": {
    coords: {
      lat: 51.48760620725085,
      lng: 0.04293921118797783,
    },
    zoom: 14,
  },
  Education: {
    coords: {
      lat: 51.48760620725085,
      lng: 0.04293921118797783,
    },
    zoom: 14,
  },
  "Further Education": {
    coords: {
      lat: 51.499385099898845,
      lng: 0.017530803562535446,
    },
    zoom: 13,
  },
  Amenities: {
    coords: {
      lat: 51.487059093921474,
      lng: 0.05085092330934238,
    },
    zoom: 14,
  },
  Destinations: {
    coords: {
      lat: 51.49617073147434,
      lng: 0.016110952148453705,
    },
    zoom: 14,
  },
}

export const MapSidebar: React.FC<{
  map: google.maps.Map | null
  categories: string[]
  withDirections: boolean
  mainMarker: Place
  places: Place[]
  panel: any
}> = ({ map, withDirections, places, panel, categories, mainMarker }) => {
  const contentControls = useStore((s) => s.contentControls)

  const {
    routeSteps,
    activeSection,
    setActiveSection,
    activePOI,
    setActivePOI,
    setTravelMode,
  } = useMapStore((state: MapState) => ({
    routeSteps: state.routeSteps,
    activePOI: state.activePOI,
    setActivePOI: state.setActivePOI,
    activeSection: state.activeSection,
    setActiveSection: state.setActiveSection,
    setTravelMode: state.setTravelMode,
  }))

  const categoryIndex = contentControls?.index
  const currentCategory = categories[categoryIndex]

  const filteredPlaces = useMemo(() => {
    if (currentCategory === "All") {
      return places
    } else {
      return places.filter((place) => place.category === currentCategory)
    }
  }, [places, categoryIndex])

  const handleBoxClick = useCallback(
    (place: Place) => {
      setActivePOI(place)
    },
    [setActivePOI],
  )

  const boxesRef = useRef<(HTMLDivElement | null)[]>([])
  console.log(currentCategory)

  useEffect(() => {
    if (activePOI) {
      const index = filteredPlaces.findIndex(
        (place) => place.name === activePOI.name,
      )
      if (index !== -1 && boxesRef.current[index]) {
        boxesRef.current[index]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }

    if (currentCategory && categoriesZoomConfig[currentCategory]) {
      const config = categoriesZoomConfig[currentCategory]
      console.log("CONFIG", config)

      map.setOptions({
        center: mainMarker.coordinates,
        zoom: config.zoom,
      })
    }
  }, [filteredPlaces, activePOI])

  useEffect(() => {
    if (activePOI) {
      setActivePOI(null)
    }
  }, [contentControls])

  const onClose = () => {
    setTravelMode("WALKING")
    setActiveSection("pois")
    setActivePOI(null)
  }

  return (
    <>
      <Boxes
        className={`boxes ${activeSection === "pois" ? "visible" : "hidden"}`}
      >
        {filteredPlaces?.map((place: Place, index: number) => (
          <Box
            className={`map-poi-box ${
              place?.name === activePOI?.name ? "active" : ""
            }`}
            key={place + index.toString()}
            ref={(el) => (boxesRef.current[index] = el)}
            active={place?.name === activePOI?.name ? 1 : undefined}
            onClick={() => {
              handleBoxClick(place)
            }}
          >
            <div className="top-section">
              <div className="info">
                <div className="title">{place.name}</div>
                <div className="subtitle">{place?.description}</div>
              </div>
              <div className="poi-indicator">
                {index < 9 ? `0${index + 1}` : index + 1}
                <div className="corner top-left"></div>
                <div className="corner bottom-right"></div>
              </div>
            </div>

            <IconLabel
              icon={`/assets/icons/distance.svg`}
              iconColor="#252525d6"
              label={averageDistance(place?.distances)}
              variant="ghost"
            />

            <div className="divider" />
            <div className="times">
              <IconLabel
                icon={`/assets/icons/walking.svg`}
                iconColor="#252525d6"
                label={place?.distances?.WALKING?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/driving.svg`}
                iconColor="#252525d6"
                label={place?.distances?.DRIVING?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/tube.svg`}
                iconColor="#252525d6"
                label={place?.distances?.TRANSIT?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/cycling.svg`}
                iconColor="#252525d6"
                label={place?.distances?.BICYCLING?.duration}
                variant="ghost"
              />
            </div>

            {withDirections && (
              <TextButton
                color="var(--black)"
                style={{
                  width: "100%",
                  marginTop: "1em",
                  fontFamily: "ThirdFont-Regular",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
                variant="outline"
                onClick={() => {
                  setActiveSection("directions")
                  setActivePOI(place)
                }}
              >
                Get Directions
              </TextButton>
            )}
          </Box>
        ))}
      </Boxes>
      <Directions
        className={
          withDirections === true && activeSection === "directions"
            ? "visible"
            : "hidden"
        }
      >
        <DirectionsPanel
          panelRef={panel}
          routeSteps={routeSteps}
          onClose={onClose}
        />
      </Directions>
    </>
  )
}

const Boxes = styled.div``

const Box = styled.div<{ active: number | undefined }>`
  min-height: 15em;
  width: 100%;
  padding: 2rem;
  flex: 0 0 auto;
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  color: var(--black);
  font-family: ThirdFont-Regular;

  .label {
    font-weight: 700;
  }

  .divider {
    height: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    margin: 1rem 0;
  }

  .info {
    width: calc(100% - 3em);
  }

  .get-directions {
    width: 100%;
    background-color: transparent;
    border: 1px solid #fff;
    padding: 0.5em 1em;
    font-size: 0.7em;
    cursor: pointer;
    text-transform: uppercase;
  }

  .top-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-family: PrimaryFont;
  }

  .title {
    font-size: 1.5em;
    font-weight: bold;
    transition: color 0.3s;
  }

  .subtitle {
    font-size: 1.1em;
    opacity: 0.7;
    padding: 0.5em 0;
  }

  .distance {
  }

  .times {
    display: grid;
    grid-template-columns: 1fr 1fr;

    .time {
      display: flex;
      gap: 1em;
      padding: 1em 0;

      & span {
        font-weight: bold;
      }
    }
  }

  .poi-indicator {
    position: absolute;
    top: 0;
    right: 0;
    height: 4em;
    width: 4em;
    min-height: 3em;
    min-width: 3em;
    background-color: #f6f5e2;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s;

    .corner {
      opacity: ${(props) => (props.active ? 1 : 0)};
      position: absolute;
      width: 10px;
      height: 10px;
      transition: all 0.3s;
    }
  }
`

const Directions = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: var(--secondary);

  &.visible {
    opacity: 1;
    z-index: 1;
    transition: all 1s;
  }

  &.hidden {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s;
  }

  .close {
    position: absolute;
    width: 100%;
    bottom: 0;
    border: 1px solid #fff;
    background-color: var(--accent);
    padding: 0.5em 1em;
    font-size: 1em;
    cursor: pointer;
    text-transform: uppercase;
  }
`
